import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  CheckCircle2, 
  FileText,
  Clock,
  Download,
  Shield,
  Zap,
  Gift,
  Star,
  BookOpen,
  FileCheck,
  Users,
  RefreshCw,
  MessageSquare,
  ArrowRight,
  Package,
  BadgeCheck
} from 'lucide-react';

// Was im Paket enthalten ist
const packageContents = [
  { icon: BookOpen, title: 'Vollständiges QM-Handbuch', description: 'Alle Kapitel nach AZAV-Anforderungen' },
  { icon: FileText, title: 'Prozessbeschreibungen', description: 'Dokumentierte Abläufe für den Trägerbetrieb' },
  { icon: FileCheck, title: 'Verfahrensanweisungen', description: 'Konkrete Handlungsanweisungen für Mitarbeiter' },
  { icon: Download, title: 'Formulare & Checklisten', description: 'Sofort einsetzbare Vorlagen für den Alltag' },
  { icon: Package, title: 'Maßnahmekonzept-Vorlage', description: 'Struktur für Ihre Bildungsmaßnahmen' },
  { icon: RefreshCw, title: 'Kostenlose Updates', description: 'Bei Änderungen der AZAV-Anforderungen' }
];

// Vorteile
const benefits = [
  'Sofort einsatzbereit – keine lange Entwicklungszeit',
  'Auditfest strukturiert nach AZAV-Vorgaben',
  'Praxiserprobt bei echten Zertifizierungsaudits',
  'Anpassbar an Ihre individuelle Trägerstruktur',
  'Inklusive Ausfüllhilfen und Beispieltexte',
  'Word-Format – einfach zu bearbeiten'
];

// Probleme ohne Musterunterlagen
const problems = [
  'Monatelange Entwicklungszeit für QM-Dokumente',
  'Unsicherheit, ob alle Anforderungen erfüllt sind',
  'Risiko von Abweichungen im Audit',
  'Hohe Kosten für individuelle Beratung',
  'Fehlende Struktur und Orientierung'
];

// Für wen geeignet
const targetGroups = [
  { title: 'Neugründer', description: 'Schneller Start mit fertigem QM-System' },
  { title: 'Bestehende Träger', description: 'Überarbeitung oder Ergänzung der Dokumentation' },
  { title: 'Träger vor dem Audit', description: 'Lücken schließen und Struktur verbessern' },
  { title: 'Berater & Consultants', description: 'Grundlage für eigene Beratungsprojekte' }
];

// FAQ
const faqs = [
  {
    question: 'In welchem Format erhalte ich die Unterlagen?',
    answer: 'Alle Dokumente werden als Word-Dateien (.docx) geliefert. So können Sie diese einfach an Ihre Bedürfnisse anpassen.'
  },
  {
    question: 'Sind die Unterlagen auf dem aktuellen Stand?',
    answer: 'Ja, die Musterunterlagen entsprechen den aktuellen AZAV-Anforderungen. Bei Änderungen erhalten Sie kostenlose Updates.'
  },
  {
    question: 'Kann ich die Unterlagen für mehrere Träger nutzen?',
    answer: 'Die Lizenz gilt für einen Träger. Für weitere Träger oder Beratungsprojekte sprechen Sie uns bitte an.'
  },
  {
    question: 'Wie läuft die 45-Minuten-Beratung ab?',
    answer: 'Nach dem Kauf vereinbaren wir einen Termin für ein Videogespräch. Dort besprechen wir Ihre Fragen und geben konkrete Tipps zur Anpassung.'
  },
  {
    question: 'Garantiert das Paket eine erfolgreiche Zulassung?',
    answer: 'Die Musterunterlagen sind eine solide Grundlage. Die erfolgreiche Zulassung hängt auch von der korrekten Anpassung und Umsetzung ab. Dabei unterstützen wir Sie gerne.'
  }
];

const Musterunterlagen = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="musterunterlagen-page">
      <Navbar />
      <div className="hero-glow" />
      <main className="pt-20 relative">
        
        {/* 1. Hero Section - Lebendiges Layout */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="text-primary text-sm font-semibold uppercase tracking-widest mb-6 block">
                  Sofort startklar
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.1] mb-6" data-testid="musterunterlagen-headline">
                  Fertiges{' '}
                  <span className="text-primary">QM-Handbuch</span>{' '}
                  nach AZAV
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed mb-6 max-w-xl">
                  Sparen Sie Monate an Entwicklungszeit. Starten Sie mit einem auditreifen QM-System, das bei echten Zertifizierungen bestanden hat.
                </p>
                <p className="text-lg text-white font-medium mb-8">
                  Inklusive 45 Minuten persönlicher Beratung – kostenlos.
                </p>
                
                {/* Preis */}
                <div className="mb-8">
                  <div className="inline-flex items-baseline gap-2">
                    <span className="text-4xl md:text-5xl font-heading font-bold text-primary">349,00€</span>
                    <span className="text-muted-foreground text-lg">zzgl. MwSt.</span>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-8 py-6 text-lg transition-all duration-300 cursor-pointer"
                    data-testid="hero-cta-button"
                    onClick={() => {/* Payment-Integration kommt später */}}
                  >
                    Jetzt kaufen
                  </Button>
                  <Link to="/kontakt">
                    <Button 
                      variant="outline"
                      className="border-white/20 hover:bg-white/5 text-white font-semibold rounded-lg px-8 py-6 text-lg transition-all duration-300 w-full"
                      data-testid="hero-contact-button"
                    >
                      Fragen? Kontakt aufnehmen
                    </Button>
                  </Link>
                </div>
              </motion.div>

              {/* Right - Paket Preview */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="hidden lg:block"
              >
                <div className="bg-card/20 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
                  <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                    <Package className="w-5 h-5 text-primary" />
                    Im Paket enthalten:
                  </h3>
                  <div className="flex flex-col gap-4">
                    {[
                      'Vollständiges QM-Handbuch',
                      'Prozessbeschreibungen',
                      'Formulare & Checklisten',
                      'Kostenlose Updates'
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3 p-4 bg-white/[0.02] rounded-xl border border-white/5">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-white">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 p-4 bg-primary/10 rounded-xl border border-primary/20">
                    <div className="flex items-center gap-3">
                      <Gift className="w-6 h-6 text-primary" />
                      <div>
                        <div className="font-semibold text-white">Bonus: 45 Min. Beratung</div>
                        <div className="text-sm text-muted-foreground">Persönliches Videogespräch inklusive</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 2. Problem Section - LIGHT SECTION */}
        <section className="py-12 md:py-16 bg-gray-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-gray-900 mb-4">
                Kennen Sie das?
              </h2>
              <p className="text-gray-600 text-lg">
                Viele Bildungsträger kämpfen mit denselben Herausforderungen:
              </p>
            </motion.div>

            <div className="flex flex-col gap-6">
              {problems.map((problem, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="flex items-center gap-4 p-5 bg-red-50 border border-red-200 rounded-xl"
                >
                  <div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                  <span className="text-gray-700">{problem}</span>
                </motion.div>
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-center text-xl text-gray-900 font-medium mt-12"
            >
              Es gibt einen einfacheren Weg.
            </motion.p>
          </div>
        </section>

        {/* 3. Lösung - Was im Paket enthalten ist */}
        <section className="py-12 md:py-16 bg-[#050505]">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-16"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-white mb-4">
                Das komplette Musterunterlagen-Paket
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Alles, was Sie für ein auditreifes QM-System nach AZAV benötigen – fix und fertig zum Anpassen.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packageContents.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="bg-card/30 border border-white/10 rounded-2xl p-6 hover:border-primary/30 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 4. Vorteile - LIGHT SECTION */}
        <section className="py-12 md:py-16 bg-gray-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-gray-900">
                Ihre Vorteile
              </h2>
            </motion.div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <ul className="space-y-5">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.08 }}
                    className="flex items-center gap-4"
                  >
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-gray-900">{benefit}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 5. Bonus - 45 Minuten Beratung */}
        <section className="py-12 md:py-16 bg-[#050505]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-2xl p-8 md:p-12"
            >
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="w-16 h-16 rounded-2xl bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
                  <Gift className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 text-primary text-sm font-medium mb-4">
                    <Star className="w-4 h-4" />
                    Inklusive Bonus
                  </div>
                  <h2 className="text-2xl md:text-3xl font-heading font-semibold text-white mb-4">
                    45 Minuten persönliche Beratung
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Bei jedem Kauf erhalten Sie eine kostenlose Beratungssession. Wir besprechen Ihre individuelle Situation 
                    und geben konkrete Tipps zur Anpassung der Unterlagen an Ihren Träger.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3 text-white">
                      <CheckCircle2 className="w-5 h-5 text-primary" />
                      Individuelle Fragen klären
                    </li>
                    <li className="flex items-center gap-3 text-white">
                      <CheckCircle2 className="w-5 h-5 text-primary" />
                      Konkrete Anpassungstipps erhalten
                    </li>
                    <li className="flex items-center gap-3 text-white">
                      <CheckCircle2 className="w-5 h-5 text-primary" />
                      Audit-Vorbereitung besprechen
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 6. Für wen geeignet - LIGHT SECTION */}
        <section className="py-12 md:py-16 bg-gray-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-gray-900">
                Für wen ist das Paket geeignet?
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {targetGroups.map((group, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
                >
                  <h3 className="text-lg font-heading font-semibold text-gray-900 mb-2">{group.title}</h3>
                  <p className="text-gray-600">{group.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 7. Social Proof / Vertrauen */}
        <section className="py-12 md:py-16 bg-[#050505]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-white mb-4">
                Praxiserprobt und auditfest
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Die Musterunterlagen basieren auf unserer Erfahrung aus echten AZAV-Zertifizierungen.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: '50+', label: 'Träger begleitet' },
                { value: '0', label: 'Abweichungen' },
                { value: '100%', label: 'Zulassungsquote' },
                { value: '5+', label: 'Jahre Erfahrung' }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-card/30 border border-white/10 rounded-xl p-6 text-center"
                >
                  <div className="text-3xl font-heading font-bold text-primary mb-1">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 8. FAQ - LIGHT SECTION */}
        <section className="py-12 md:py-16 bg-gray-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-gray-900">
                Häufige Fragen
              </h2>
            </motion.div>

            <div className="flex flex-col gap-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
                >
                  <h3 className="text-lg font-heading font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 9. CTA Section */}
        <section className="py-12 md:py-16 bg-[#050505] relative">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-br from-card/50 to-card/30 border border-white/10 rounded-2xl p-8 md:p-12 text-center"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-8">
                <BadgeCheck className="w-7 h-7 text-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-white mb-4">
                Bereit für Ihr auditreifes QM-System?
              </h2>
              <p className="text-muted-foreground leading-relaxed max-w-xl mx-auto mb-4">
                Holen Sie sich das komplette Musterunterlagen-Paket und starten Sie mit einer soliden Grundlage.
              </p>
              <p className="text-white font-medium mb-6">
                Inklusive 45 Minuten persönlicher Beratung.
              </p>
              
              {/* Preis */}
              <div className="mb-8">
                <div className="inline-flex items-baseline gap-2">
                  <span className="text-3xl md:text-4xl font-heading font-bold text-primary">349,00€</span>
                  <span className="text-muted-foreground">zzgl. MwSt.</span>
                </div>
              </div>
              
              <Button 
                className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-10 py-6 text-lg transition-all duration-300 cursor-pointer"
                data-testid="footer-cta-button"
                onClick={() => {/* Payment-Integration kommt später */}}
              >
                Jetzt kaufen
              </Button>
              
              <p className="text-muted-foreground text-sm mt-6">
                Sie erhalten ein individuelles Angebot basierend auf Ihrer Trägerstruktur.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 10. Cross-Selling: Beratung - LIGHT SECTION */}
        <section className="py-12 md:py-16 bg-gray-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <h2 className="text-xl md:text-2xl font-heading font-semibold tracking-tight text-gray-900 mb-4">
                Lieber individuelle Begleitung?
              </h2>
              <p className="text-gray-600 leading-relaxed max-w-xl mx-auto mb-8">
                Wenn Sie eine persönliche Betreuung bei der Entwicklung und Umsetzung Ihres QM-Systems wünschen, 
                bieten wir auch individuelle Beratungspakete an.
              </p>
              <Link to="/leistungen" className="inline-flex items-center gap-2 text-primary hover:text-[#05b873] transition-colors font-medium">
                Beratungsangebote ansehen
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </section>

      </main>
      <Footer />
      <div className="noise-overlay" />
    </div>
  );
};

export default Musterunterlagen;
